import {useCompanies, useParentCompanies} from '@api'
import {activeCell, countryCell, isSuperAdmin, renderClickableCell, useViewNavigate} from '@common'
import {NavigationPathKey, TranslationKey} from '@enums'
import {GridColDef} from '@mui/x-data-grid'
import {pageLinksAtom} from '@state'

import {useEffect, useMemo} from 'react'

import {useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'

export interface CompanyTableData {
  name: string
  description: string
  country: string
  active: boolean
}

export function useCompanySearchModel() {
  const setTitleLinks = useSetAtom(pageLinksAtom)
  const {t} = useTranslation()
  const navigate = useViewNavigate()

  const {data} = useCompanies()
  const {data: parentCompanies} = useParentCompanies()

  const rows = useMemo(() => {
    if (!data) return []
    return data.map(item => ({
      name: item.name,
      description: item.description,
      parentCompany: parentCompanies?.find(parentItem => parentItem.id === item.parentCompanyId)?.name,
      country: item.country || '',
      active: item.active,
      id: item.id,
    }))
  }, [data, parentCompanies])

  useEffect(
    () =>
      setTitleLinks([
        {title: t(TranslationKey['Admin panel']), link: NavigationPathKey.ADMIN_PANEL},
        {title: t(TranslationKey.Resources), link: NavigationPathKey.RESOURCES_DASHBOARD},
        {
          title: t(TranslationKey['Company search']),
          link: NavigationPathKey.COMPANY_SEARCH,
        },
      ]),
    [],
  )

  /**
   * Company table columns definition
   */
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t(TranslationKey.Name),
      flex: 1,
      hideable: false,
      renderCell: params =>
        renderClickableCell(params, () => navigate(`${NavigationPathKey.COMPANY_VIEW}/${params.row.id}`)),
    },
    countryCell(t),
    {field: 'parentCompany', headerName: t(TranslationKey['Amplio partner']), flex: 1},
    {field: 'description', headerName: t(TranslationKey.Description), flex: 2},
    activeCell(t),
  ]

  /**
   * Create button should be visible only for Super Administrators
   */
  const showCreateButton = isSuperAdmin()

  return {navigate, t, showCreateButton, rows, columns}
}
